import { useEffect, useState } from "react";
import {
  Container, Title, Text, Stack, Group, ThemeIcon, Loader, Center, Anchor, Alert, Button,
  Spoiler, Box,
} from "@mantine/core";
import { IconHistory, IconLock } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { useLocation } from "wouter";
import { getActivityFeed, markFeedSeen } from "../api";
import { usePlan } from "../hooks/usePlan";
import { useStore } from "../store";
import { requestUpgrade } from "../Pricing/upgrade";
import { entryTypeConfig, ActivityBody } from "../_Shared/activity";
import { relativeTime, dayKey, dayLabel } from "../_Shared/time";
import { CandidateAvatar } from "../Candidates/CandidateAvatar";
import UserInitialsAvatar from "../components/UserInitialsAvatar";

const PAGE_SIZE = 50; // entries per fetch; "Load more" pages backwards from the oldest seen

// Leading-avatar footprint. Mantine's size scale differs per component (Avatar "md" = 38px,
// ThemeIcon "md" = 28px), so the human-initials avatar and the system/source icon would render
// at different widths and jog the body-text indentation between rows. Pin both to one pixel size.
const FEED_AVATAR_SIZE = 38;

// Group a newest-first list into calendar-day buckets, preserving order.
function groupByDay(entries, lang, t) {
  const groups = [];
  for (const e of entries) {
    const key = dayKey(e.createdAt);
    if (!groups.length || groups[groups.length - 1].key !== key)
      groups.push({ key, label: dayLabel(e.createdAt, lang, t), items: [] });
    groups[groups.length - 1].items.push(e);
  }
  return groups;
}

function FeedRow({ e, t, lang, onCandidate }) {
  const config = entryTypeConfig[e.type] || entryTypeConfig.note;
  const Icon = config.icon;
  const human = !!e.createdByName; // a person did it → their initials; else a machine/source icon

  return (
    <Group gap="sm" align="flex-start" wrap="nowrap" className="feed-row">
      {human ? (
        <UserInitialsAvatar name={e.createdByName} size={FEED_AVATAR_SIZE} />
      ) : (
        <ThemeIcon variant="light" color={config.color} size={FEED_AVATAR_SIZE} radius="xl">
          <Icon size={18} />
        </ThemeIcon>
      )}
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Spoiler maxHeight={64} showLabel={t("show_more")} hideLabel={t("show_less")}
          styles={{ control: { fontSize: "var(--mantine-font-size-xs)" } }}>
          <Text size="sm" style={{ whiteSpace: "pre-wrap" }}><ActivityBody entry={e} t={t} /></Text>
        </Spoiler>
        <Group gap={6} mt={5} wrap="nowrap" style={{ overflow: "hidden" }}>
          {e.candidateName && (
            <Group gap={6} wrap="nowrap" style={{ minWidth: 0 }}>
              <CandidateAvatar
                candidate={{ id: e.candidateId, photoBlobId: e.candidatePhotoBlobId, name: e.candidateName }}
                size={20} radius="xl" />
              <Anchor size="xs" lineClamp={1} href={`/candidates/${e.candidateId}`}
                onClick={(ev) => { ev.preventDefault(); onCandidate(e.candidateId); }}>
                {e.candidateName}
              </Anchor>
            </Group>
          )}
          <Text size="xs" c="dimmed" style={{ whiteSpace: "nowrap" }}>
            {e.candidateName ? "· " : ""}{relativeTime(e.createdAt, lang)}
          </Text>
          {e.projectTitle && <Text size="xs" c="dimmed" lineClamp={1}>· {e.projectTitle}</Text>}
        </Group>
      </Box>
    </Group>
  );
}

/**
 * Tenant-wide activity feed — the team's "who changed what, when" across every candidate. Pro+ only
 * (the collaboration feature); the nav item is hidden otherwise, and this page shows an upgrade
 * prompt for anyone who reaches it directly. Day-grouped, with actor + candidate avatars, relative
 * timestamps and expandable long entries. Each line links to the candidate it belongs to.
 */
export default function ActivityFeed() {
  const { t, i18n } = useTranslation("Veft");
  const { canUseCollaboration } = usePlan();
  const setFeedUnread = useStore((s) => s.setFeedUnread);
  const [, setLocation] = useLocation();
  const [entries, setEntries] = useState([]);
  const [nextBefore, setNextBefore] = useState(null); // keyset cursor; null = no older entries
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    if (!canUseCollaboration) { setLoading(false); return undefined; }
    let cancelled = false;
    getActivityFeed(PAGE_SIZE)
      .then((d) => { if (!cancelled) { setEntries(d?.entries ?? []); setNextBefore(d?.nextBefore ?? null); } })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    // Opening the feed marks it seen and clears the nav unread badge.
    markFeedSeen().then(() => { if (!cancelled) setFeedUnread(0); }).catch(() => {});
    return () => { cancelled = true; };
  }, [canUseCollaboration, setFeedUnread]);

  async function loadMore() {
    if (!nextBefore || loadingMore) return;
    setLoadingMore(true);
    try {
      const d = await getActivityFeed(PAGE_SIZE, nextBefore);
      const more = d?.entries ?? [];
      // Dedupe by id — a write whose entry-events straddle the page boundary could otherwise repeat.
      setEntries((prev) => {
        const seen = new Set(prev.map((e) => e.id));
        return [...prev, ...more.filter((e) => !seen.has(e.id))];
      });
      setNextBefore(d?.nextBefore ?? null);
    } catch {
      /* leave state as-is; the button stays for a retry */
    } finally {
      setLoadingMore(false);
    }
  }

  const header = (
    <Title order={3} mb="xs">
      <Group gap="xs"><IconHistory size={22} /> {t("activity_feed_title")}</Group>
    </Title>
  );

  if (!canUseCollaboration) {
    return (
      <Container size="md" py="md">
        {header}
        <Alert color="yellow" icon={<IconLock size={18} />} title={t("activity_feed_locked_title")} mt="md">
          <Text size="sm" mb="sm">{t("activity_feed_locked_body")}</Text>
          <Button size="xs" onClick={() => requestUpgrade(t("activity_feed_locked_body"))}>
            {t("activity_feed_upgrade_cta")}
          </Button>
        </Alert>
      </Container>
    );
  }

  const groups = groupByDay(entries, i18n.language, t);
  const goCandidate = (id) => setLocation(`/candidates/${id}`);

  return (
    <Container size="md" py="md">
      {header}
      <Text size="sm" c="dimmed" mb="lg">{t("activity_feed_subtitle")}</Text>
      {loading ? (
        <Center py="xl"><Loader /></Center>
      ) : entries.length === 0 ? (
        <Center py="xl"><Text size="sm" c="dimmed">{t("activity_feed_empty")}</Text></Center>
      ) : (
        <>
          <Stack gap="xl">
            {groups.map((g) => (
              <div key={g.key}>
                <Text className="feed-day-label" size="xs" fw={600} tt="uppercase" c="dimmed" mb="xs">
                  {g.label}
                </Text>
                <Stack gap={2}>
                  {g.items.map((e) => (
                    <FeedRow key={e.id} e={e} t={t} lang={i18n.language} onCandidate={goCandidate} />
                  ))}
                </Stack>
              </div>
            ))}
          </Stack>
          {nextBefore && (
            <Center mt="lg">
              <Button variant="default" size="sm" loading={loadingMore} onClick={loadMore}>
                {t("load_more")}
              </Button>
            </Center>
          )}
        </>
      )}
    </Container>
  );
}
